import React, { useCallback, useState, useEffect } from 'react';
import { Box, Text } from '@goodrx/matisse';
import { debounce } from 'lodash';
import { HmaConsentModal } from 'app/components/legal';
import { useToggledState } from 'app/hooks';
import { DiscoverPrices } from 'app/components/pharmacies/DiscoverPrices';
import { useSynapse } from 'app/lib/synaptik';
import { SearchInput } from 'app/components/core';
import { PharmacyList } from 'app/components/widgets/pharmacy-select/PharmacyList';
import { Experiment, ExperimentVariation, useExperiment } from 'app/components/Experiment';

interface PharmacyPickupViewProps {
  onSearch: any;
  initialAddress?: string;
  pharmacies: any;
  onSelect: any;
  searching: any;
  searchState?: string;
  visitPrescriptionId?: number;
}

export const PharmacyPickupView = ({
  onSearch,
  initialAddress = '',
  pharmacies,
  onSelect,
  searching,
  searchState,
}: PharmacyPickupViewProps): JSX.Element => {
  const search = useCallback(debounce(onSearch, 250), [onSearch]);
  const [address, setAddress] = useState(initialAddress);
  const [searchValue, setSearchValue] = useState('');
  const [isHmaModalOpen, toggleHmaModal] = useToggledState(false);
  const [consentedToHma] = useSynapse(({ user }: any) => [user.consentedToHma()]);
  const [bigFiveExperimentVariant] = useExperiment('BIG_5_PHARMACY_TEST');
  const big5 = bigFiveExperimentVariant === 'Test_big_5' ? 1 : 0;
  const placeholder = !searchState ? 'Search for a pharmacy...' : 'Pharmacy Name';

  const isExperimentReady = () => bigFiveExperimentVariant !== null;

  const failSafe = () => {
    if (!isExperimentReady()) {
      search({ address: initialAddress, big5 });
    }
  };

  useEffect(() => {
    // Wait for experiment to be ready
    if (isExperimentReady()) {
      search({ address: initialAddress, big5 });
    } else {
      setTimeout(failSafe, 1000);
    }
  }, [initialAddress, consentedToHma, bigFiveExperimentVariant, big5]);

  return (
    <Box mb="12px">
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ _: 'column', md: 'row' }}
        alignItems={{ _: 'flexStart', md: 'center' }}
        mb={3}>
        <Text fontSize="20px" lineHeight="27px" fontWeight="600">
          Pick Up Today
        </Text>

        <Box
          display="flex"
          alignItems="center"
          flexDirection={{ _: 'column', xs: 'row' }}
          mt={{ _: 3, md: 0 }}
          style={{ width: '100%' }}
          maxWidth={{ _: 'initial', md: '500px' }}>
          <Box maxWidth={{ _: 'initial', md: '160px' }} style={{ width: '100%' }}>
            <SearchInput
              data-qa="search_by_zip_input"
              name="address"
              autoComplete="off"
              value={address}
              placeholder="Zip Code"
              onChange={(_: any, value: string) => {
                setAddress(value);
                if (search) {
                  search({
                    search: searchValue,
                    address: value,
                    big5,
                  });
                }
              }}
            />
          </Box>

          <Box ml={{ _: 0, md: 3 }} mt={{ _: 3, xs: 0, md: 0 }} style={{ width: '100%' }}>
            <SearchInput
              data-qa="search_by_pharmacy_name_input"
              name="search"
              autoComplete="off"
              placeholder={placeholder}
              value={searchValue}
              onChange={(_: any, value: string) => {
                setSearchValue(value);

                if (search) {
                  search({
                    search: value,
                    address,
                    big5,
                  });
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {!consentedToHma && (
        <Box mb={4}>
          <DiscoverPrices onBannerClick={toggleHmaModal} />
        </Box>
      )}

      <Box>
        <Experiment name="PHARMACY_DISPLAY_TEST_ASCENDING_PRICE">
          <ExperimentVariation<'PHARMACY_DISPLAY_TEST_ASCENDING_PRICE'> variation="Control_distance_based" default>
            <PharmacyList onSelect={onSelect} pharmacies={pharmacies} loading={searching} />
          </ExperimentVariation>
          <ExperimentVariation<'PHARMACY_DISPLAY_TEST_ASCENDING_PRICE'> variation="Test_price_based">
            <PharmacyList onSelect={onSelect} pharmacies={pharmacies} loading={searching} sortByPrice />
          </ExperimentVariation>
        </Experiment>
      </Box>

      <HmaConsentModal
        isOpen={isHmaModalOpen}
        onClose={toggleHmaModal}
        attributionSource="Consent Modal - Pharmacy Selection"
      />
    </Box>
  );
};
